import { useEffect, useRef } from 'react';
import type { AppSnapshot } from '../../app/state';
import { initialsOf } from '../../components/SubjectPicker';
import {
  dueAfterParent,
  firstNameOf,
  formatDue as formatDueOn,
  hasNotes,
  isDueSoon,
  isOverdue as isOverdueOn,
  relativeDay,
  rollupOf,
  type Todo,
} from '../../lib/todos';
import { todoText, todoTextf } from '../../i18n/todos';

/**
 * ONE TASK, AS A ROW AND AS A CARD (V0.51.1).
 *
 * The V0.50 row was one line — checkbox, title, avatars, date — and the notes hung below it on
 * a caret. Sub-tasks now want that space underneath, so the two could not both live there, and
 * the row had to say more in itself. The user chose the arrangement in a proof of concept; what
 * follows is that arrangement, not a set of options.
 *
 * TWO LINES. The title and its controls on the first, and a faint meta line under it carrying
 * the due day, who it is for by first name, and a mark when there are notes to open. A row is
 * read far more often than it is acted on, so what it SAYS matters more than how compact it is.
 * The meta line is dropped entirely when it would be empty, which is what keeps a bare list of
 * titles from growing a row of blank space per item.
 *
 * ONE CLICK OPENS THE CARD, TWO OPEN THE EDITOR — the user's rule. Every row has a card now, so
 * the V0.50.1 "only a row with notes can be expanded" rule is gone: notes were never the only
 * thing a task holds, and who it is for and who wrote it were reachable from nowhere.
 *
 * THE AUTHOR IS A PILL ON EVERY ROW, outlined and carrying a pen, so it never reads as an
 * assignee's filled avatar. Both shapes draw it. It is omitted only when the account has been
 * erased or the directory cannot name it — a pill saying nothing is worse than no pill.
 */

/** Today, bound: `lib/todos.ts` takes it as a parameter and the clock is read at the top. */
export interface RowContext {
  s: AppSnapshot;
  today: string;
  /** Every task on this node — the row needs its siblings to count a rollup and its parent to
   *  judge a due date against. */
  all: readonly Todo[];
}

const isOverdue = (t: Todo, today: string) => isOverdueOn(t.dueDate, t.state, today);

/** The due day in words: a relative one where there is one, else `25 Sep`. */
export function dueWordOf(due: string, today: string, locale: string): string {
  const rel = relativeDay(due, today);
  return rel ? todoText(rel, locale) : formatDueOn(due, today);
}

/** Who a row is aimed at, teams first — the V0.50.1 order, unchanged. */
export function assigneeChips(
  s: AppSnapshot,
  todo: Todo,
): { key: string; name: string; group: boolean }[] {
  return [
    ...todo.assigneeGroupIds.map((id) => ({
      key: 'g:' + id,
      name: s.groupDirectory.find((x) => x.id === id)?.name ?? id,
      group: true,
    })),
    ...todo.assigneeUserIds.map((id) => ({
      key: 'u:' + id,
      name: s.userDirectory.find((x) => x.id === id)?.displayName ?? id,
      group: false,
    })),
  ];
}

/** The author's display name, or null when there is nobody nameable to show. */
function authorName(s: AppSnapshot, todo: Todo): string | null {
  if (!todo.createdBy) return null;
  return s.userDirectory.find((u) => u.id === todo.createdBy)?.displayName ?? null;
}

function AuthorPill({ ctx, todo }: { ctx: RowContext; todo: Todo }) {
  const name = authorName(ctx.s, todo);
  if (!name) return null;
  const locale = ctx.s.currentLocale;
  return (
    <span
      className="todo-author"
      title={todoTextf('writtenBy', locale, {
        s: name,
        d: formatDueOn(todo.createdAt.slice(0, 10), ctx.today),
      })}
    >
      <svg aria-hidden="true">
        <use href="#i-edit" />
      </svg>
      {firstNameOf(name)}
    </span>
  );
}

/**
 * The parent's checkbox, half-filled while some of its children are done.
 *
 * `indeterminate` is a DOM property with no HTML attribute and no React prop, so it can only be
 * set through a ref — which is why this is its own component rather than a ternary inline.
 */
function StateBox({
  checked,
  indeterminate,
  disabled,
  title,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  title?: string;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="todo-check"
      checked={checked}
      disabled={disabled}
      title={title}
      onChange={onChange}
      /* The row's own click opens the card; ticking is not that. */
      onClick={(e) => e.stopPropagation()}
    />
  );
}

export interface TaskRowProps {
  ctx: RowContext;
  todo: Todo;
  depth: 0 | 1;
  busy: boolean;
  canTick: boolean;
  canEdit: boolean;
  /** Is the detail card open under this row? */
  open: boolean;
  /** Are this row's sub-tasks hidden? Undefined when it has none. */
  folded?: boolean;
  dragging: boolean;
  /** Where a drop would land on THIS row, or null to draw nothing. */
  dropInto: boolean;
  onOpen: () => void;
  onEditor: () => void;
  onToggle: () => void;
  onDelete: () => void;
  onFold: () => void;
  onAddSub: () => void;
  onKeyNav: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

export function TaskRow({
  ctx,
  todo,
  depth,
  busy,
  canTick,
  canEdit,
  open,
  folded,
  dragging,
  dropInto,
  onOpen,
  onEditor,
  onToggle,
  onDelete,
  onFold,
  onAddSub,
  onKeyNav,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: TaskRowProps) {
  const locale = ctx.s.currentLocale;
  const done = todo.state === 'done';
  const roll = rollupOf(ctx.all, todo.id);
  const isParent = roll.total > 0;
  const overdue = isOverdue(todo, ctx.today);
  const soon = isDueSoon(todo.dueDate, todo.state, ctx.today);
  const parent = todo.parentId ? ctx.all.find((t) => t.id === todo.parentId) : null;
  const late = dueAfterParent(todo, parent);
  const who = assigneeChips(ctx.s, todo);

  /* A done row is inert as a drag source and as a target: it has sunk to the bottom of its
     group, so "where would it land" has no useful answer, and dragging one is almost always
     a mis-grab of the row above. */
  const draggable = canEdit && !done;

  const dueClass = late ? ' conflict' : overdue ? ' overdue' : soon ? ' soon' : '';
  const meta = [
    todo.dueDate ? (
      <span
        key="due"
        className={`todo-due${dueClass}`}
        title={
          late && parent?.dueDate
            ? todoTextf('dueAfterParent', locale, { s: dueWordOf(parent.dueDate, ctx.today, locale) })
            : todoText(overdue ? 'overdue' : 'due', locale)
        }
      >
        {late ? <span aria-hidden="true">⚠ </span> : null}
        {dueWordOf(todo.dueDate, ctx.today, locale)}
      </span>
    ) : null,
    who.length ? (
      <span key="who" className="todo-whos">
        {who.map((w) => (
          <span key={w.key} className={`subj-av todo-who${w.group ? ' grp' : ''}`} title={w.name}>
            {initialsOf(w.name)}
          </span>
        ))}
        <span className="who-name">{who.map((w) => firstNameOf(w.name)).join(', ')}</span>
      </span>
    ) : null,
    hasNotes(todo) && !open ? (
      <span key="notes" className="todo-note-mark" title={todoText('hasNotes', locale)}>
        <svg aria-hidden="true">
          <use href="#i-info" />
        </svg>
      </span>
    ) : null,
  ].filter(Boolean);

  return (
    <div
      className={[
        'todorow',
        done ? 'done' : '',
        open ? 'sel' : '',
        dragging ? 'dragging' : '',
        dropInto ? 'dropinto' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-id={todo.id}
      data-depth={depth}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={onOpen}
      onDoubleClick={onEditor}
      onKeyDown={onKeyNav}
    >
      <StateBox
        checked={done}
        indeterminate={isParent && roll.done > 0 && roll.done < roll.total}
        disabled={!canTick || busy}
        title={
          !canTick
            ? todoText('notYours', locale)
            : isParent
              ? todoText(done ? 'reopenAll' : 'completeAll', locale)
              : undefined
        }
        onChange={onToggle}
      />

      {/* The caret cell is drawn on every ROOT, blank where there are no sub-tasks, so titles
          line up down the list instead of stepping in and out by fourteen pixels. */}
      {depth === 0 ? (
        isParent ? (
          <button
            type="button"
            className={`row-caret${folded ? '' : ' open'}`}
            aria-expanded={!folded}
            title={todoText(folded ? 'showSubtasks' : 'hideSubtasks', locale)}
            onClick={(e) => {
              e.stopPropagation();
              onFold();
            }}
          >
            <svg aria-hidden="true">
              <use href="#i-chev" />
            </svg>
          </button>
        ) : (
          <span className="row-caret blank" aria-hidden="true" />
        )
      ) : null}

      <span className="todo-title">{todo.title}</span>

      {isParent ? (
        <span
          className={`todo-pill${roll.done === roll.total ? ' all' : ''}`}
          title={todoTextf('subtasksDone', locale, { d: roll.done, t: roll.total })}
        >
          {roll.done}/{roll.total}
        </span>
      ) : null}

      <AuthorPill ctx={ctx} todo={todo} />

      {canEdit ? (
        <span className="todo-acts" onClick={(e) => e.stopPropagation()}>
          {/* Offered on ROOTS only: one level, so a sub-task has nothing to add under it. */}
          {depth === 0 ? (
            <button type="button" className="row-add" title={todoText('addSub', locale)} onClick={onAddSub}>
              <svg aria-hidden="true">
                <use href="#i-plus" />
              </svg>
            </button>
          ) : null}
          <button type="button" className="row-edit" title={todoText('edit', locale)} onClick={onEditor}>
            <svg aria-hidden="true">
              <use href="#i-edit" />
            </svg>
          </button>
          <button type="button" className="row-x" title={todoText('remove', locale)} onClick={onDelete}>
            ×
          </button>
        </span>
      ) : null}

      {meta.length ? (
        <div className="todo-meta">
          {meta.map((part, i) => (
            <span key={i} className="todo-meta-part">
              {i ? <span className="sep">·</span> : null}
              {part}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Everything the task holds, as a card under its row.
 *
 * Drawn for EVERY reader, including one with `VIEW_TODOS` alone: who a job is for and when it
 * is wanted is not an editing fact. The Edit button appears where editing is allowed, so
 * nobody has to discover the double-click to reach the editor.
 */
export function TaskDetail({
  ctx,
  todo,
  canEdit,
  onEditor,
}: {
  ctx: RowContext;
  todo: Todo;
  canEdit: boolean;
  onEditor: () => void;
}) {
  const locale = ctx.s.currentLocale;
  const roll = rollupOf(ctx.all, todo.id);
  const who = assigneeChips(ctx.s, todo);
  const parent = todo.parentId ? ctx.all.find((t) => t.id === todo.parentId) : null;
  const late = dueAfterParent(todo, parent);
  const author = authorName(ctx.s, todo);
  const completer = todo.completedBy
    ? (ctx.s.userDirectory.find((u) => u.id === todo.completedBy)?.displayName ?? null)
    : null;

  const rows: [string, React.ReactNode][] = [];
  rows.push([
    todoText('detailAssigned', locale),
    who.length ? (
      <>
        {who.map((w, i) => (
          <span key={w.key}>
            {i ? <span className="sep">·</span> : null}
            <span className={`subj-av todo-who${w.group ? ' grp' : ''}`}>{initialsOf(w.name)}</span> {w.name}
          </span>
        ))}
      </>
    ) : (
      <i>{todoText('detailNobody', locale)}</i>
    ),
  ]);
  rows.push([
    todoText('due', locale),
    todo.dueDate ? (
      <>
        {dueWordOf(todo.dueDate, ctx.today, locale)}
        {isOverdue(todo, ctx.today) ? <span className="todo-due overdue"> {todoText('overdue', locale)}</span> : null}
        {late && parent?.dueDate ? (
          <span className="todo-due conflict">
            {' '}
            ⚠ {todoTextf('dueAfterParent', locale, { s: dueWordOf(parent.dueDate, ctx.today, locale) })}
          </span>
        ) : null}
      </>
    ) : (
      <i>{todoText('detailNoDate', locale)}</i>
    ),
  ]);
  if (roll.total) {
    rows.push([
      todoText('subtasksLabel', locale),
      todoTextf('subtasksDone', locale, { d: roll.done, t: roll.total }),
    ]);
  }
  if (author) {
    rows.push([
      todoText('detailWrittenBy', locale),
      <>
        {author} <i>{todoText('detailOn', locale)} {formatDueOn(todo.createdAt.slice(0, 10), ctx.today)}</i>
      </>,
    ]);
  }
  if (todo.state === 'done' && todo.completedAt) {
    rows.push([
      todoText('detailDone', locale),
      <>
        {formatDueOn(todo.completedAt.slice(0, 10), ctx.today)}
        {completer ? ` ${todoText('detailBy', locale)} ${completer}` : ''}
      </>,
    ]);
  }

  return (
    <div className="todo-detail" data-todo-detail={todo.id} onClick={(e) => e.stopPropagation()}>
      <div className="todo-detail-notes">
        {hasNotes(todo) ? todo.notes : <i>{todoText('noNotes', locale)}</i>}
      </div>
      <div className="kv">
        {rows.map(([k, v], i) => (
          <span key={i} className="kv-row">
            <b>{k}</b>
            <span>{v}</span>
          </span>
        ))}
      </div>
      {canEdit ? (
        <div className="todo-detail-foot">
          <button type="button" className="pbtn" onClick={onEditor}>
            <svg className="ico" aria-hidden="true">
              <use href="#i-edit" />
            </svg>
            {todoText('editButton', locale)}
          </button>
          <span className="todo-hint">{todoText('editHint', locale)}</span>
        </div>
      ) : null}
    </div>
  );
}
